import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/api';
import CardContent from '../components/Dashboard/CardContent';
import { loadNoteContent } from './DashboardUtils';
import {
  getSelectedFolder,
  getSelectedTopic,
  getSelectedKB,
  getSelectedTag,
  getSearchKeyword
} from '../hooks/useDashboardGlobalVar';

// 判断 content 是否是文件路径的函数（与 Dashboard.js 中的定义一致）
function isFilePath(content) {
  if (!content) return false;
  return (
    typeof content === 'string' &&
    (content.startsWith('uploads/') || content.startsWith('cloud/')) &&
    (content.endsWith('.md') || content.endsWith('.txt') || content.endsWith('.html') || content.endsWith('.htm'))
  );
}

/**
 * 笔记内容独立页面
 * 通过 sessionStorage 接收 noteId（POST 方式传递，不显示在 URL 中），然后通过 POST 请求获取数据
 */
const NoteContentPage = () => {
  const { t } = useTranslation(['dashboard', 'common', 'messages']);
  
  const [selectedNote, setSelectedNote] = useState(null);
  const [realContent, setRealContent] = useState('');
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Initial tool to open in right panel
  const [initialTool, setInitialTool] = useState(null);

  // 添加防重复加载的 ref
  const loadingNoteIdRef = useRef(null);
  const isLoadNoteDataRunningRef = useRef(false);
  const hasShownNoNoteIdErrorRef = useRef(false); // 标记是否已经显示过"没有 noteId"的错误

  // 通过 POST 请求加载笔记数据
  const loadNoteData = useCallback(async (id) => {
    if (!id) return;
    
    // 防止重复加载同一个笔记
    if (loadingNoteIdRef.current === id && isLoadNoteDataRunningRef.current) {
      console.log('[NoteContentPage] 正在加载中，跳过重复请求:', id);
      return;
    }
    
    // 如果当前已加载的笔记ID相同，跳过
    if (selectedNote?.id === id && !isLoading) {
      console.log('[NoteContentPage] 笔记已加载，跳过重复请求:', id);
      return;
    }
    
    loadingNoteIdRef.current = id;
    isLoadNoteDataRunningRef.current = true;
    
    setIsLoading(true);
    setIsLoadingContent(true);
    
    try {
      // 通过 GET 请求获取笔记数据
      // const response = await apiClient.get(`/notes/${id}`);
      
      // 通过 POST 请求获取笔记数据
      // 如果后端没有 /notes/get-by-id 端点，会回退到 GET 请求
      let response;
      try {
        response = await apiClient.post('/notes/get-by-id', { noteId: id });
      } catch (postError) {
        // 如果 POST 请求失败，使用 GET 请求作为后备方案
        console.warn('POST request failed, falling back to GET:', postError);
        response = await apiClient.get(`/notes/${id}`);
      }
      
      if (response.data && response.data.id) {
        const note = response.data;
        setSelectedNote(note);
        
        // 加载笔记内容（使用 isFilePath 函数判断）
        const contentResult = await loadNoteContent(note, apiClient, t, isFilePath);
        setRealContent(contentResult.content || '');
        setIsLoadingContent(contentResult.needsLoading || false);
      } else {
        message.error(t('messages:error_loading', { defaultValue: 'Failed to load note' }));
      }
    } catch (error) {
      console.error('Failed to load note:', error);
      message.error(t('dashboard:error_loading_note_detail', { 
        defaultValue: 'Failed to load note: {{detail}}',
        detail: error.response?.data?.message || error.message 
      }));
    } finally {
      setIsLoading(false);
      setIsLoadingContent(false);
      isLoadNoteDataRunningRef.current = false;
    }
  }, [t, selectedNote, isLoading]);

  // 从 sessionStorage 获取 noteId（通过 POST 方式传递，不显示在 URL 中）
  useEffect(() => {
    let checkInterval = null;
    
    const loadNoteFromStorage = () => {
      const storedNoteId = sessionStorage.getItem('noteContent_noteId');
      const storedTool = sessionStorage.getItem('noteContent_tool');
      if (storedTool) {
        setInitialTool(storedTool);
      }
      if (storedNoteId) {
        // 如果找到了 noteId，重置错误标记
        hasShownNoNoteIdErrorRef.current = false;
        
        // 检查 noteId 是否变化
        const currentNoteId = selectedNote?.id?.toString();
        // 防止重复加载：只有在 noteId 真正变化或首次加载时才加载
        if (currentNoteId !== storedNoteId && !isLoadNoteDataRunningRef.current) {
          // noteId 变化了，重新加载
          console.log('[NoteContentPage] noteId 变化，重新加载:', { currentNoteId, storedNoteId });
          loadNoteData(storedNoteId);
        } else if (!selectedNote && !isLoadNoteDataRunningRef.current) {
          // 首次加载
          loadNoteData(storedNoteId);
        }
      } else {
        // 没有 noteId，只在首次检测到时显示错误提示
        if (!hasShownNoNoteIdErrorRef.current && !selectedNote) {
          console.error('[NoteContentPage] sessionStorage 中没有 noteId，无法加载笔记');
          message.error(t('messages:no_note_id', { defaultValue: 'No note ID provided' }));
          hasShownNoNoteIdErrorRef.current = true; // 标记已显示过错误
          setIsLoading(false);
        }
      }
    };

    // 初始加载
    loadNoteFromStorage();

    // 监听 postMessage 事件（当父窗口发送消息时触发）
    const handleMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (e.data && e.data.type === 'noteIdChanged' && e.data.noteId) {
        console.log('[NoteContentPage] 收到 noteId 变化消息，重新加载:', e.data.noteId);
        sessionStorage.setItem('noteContent_noteId', e.data.noteId);
        if (e.data.tool) {
          sessionStorage.setItem('noteContent_tool', e.data.tool);
          setInitialTool(e.data.tool);
        }
        // 只有在不重复加载时才执行
        if (!isLoadNoteDataRunningRef.current || loadingNoteIdRef.current !== e.data.noteId) {
          loadNoteData(e.data.noteId);
        }
      }
    };

    window.addEventListener('message', handleMessage);

    // 监听 storage 事件（当其他标签页修改 sessionStorage 时触发）
    const handleStorageChange = (e) => {
      if (e.key === 'noteContent_tool' && e.newValue) {
        setInitialTool(e.newValue);
      }
      if (e.key === 'noteContent_noteId' && e.newValue) {
        console.log('[NoteContentPage] 检测到 sessionStorage 变化，重新加载:', e.newValue);
        // 只有在不重复加载时才执行
        if (!isLoadNoteDataRunningRef.current || loadingNoteIdRef.current !== e.newValue) {
          loadNoteData(e.newValue);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // 定期检查 sessionStorage 变化（因为同源页面的 storage 事件可能不会触发）
    // 增加检查间隔，减少重复调用
    checkInterval = setInterval(() => {
      loadNoteFromStorage();
    }, 1000); // 改为每 1000ms 检查一次，减少频率

    return () => {
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorageChange);
      if (checkInterval) {
        clearInterval(checkInterval);
      }
    };
  }, [loadNoteData, t, selectedNote, isLoading]);

  // Edit mode handlers
  const handleStartEdit = useCallback(async () => {
    if (!selectedNote) return;
    try {
      const htmlPath = selectedNote.content_html;
      if (!htmlPath || !isFilePath(htmlPath)) {
        message.warning(t('messages:no_content', { defaultValue: 'No HTML content available for editing' }));
        return;
      }
      const res = await apiClient.post('/notes/content', { contentPath: htmlPath, mdOnly: true });
      setEditedContent(res.data?.content || '');
      setIsEditing(true);
    } catch (error) {
      console.error('Failed to load HTML content for editing:', error);
      message.error(t('messages:error_loading', { defaultValue: 'Failed to load content for editing' }));
    }
  }, [selectedNote, t]);

  const handleContentChange = useCallback((markdown) => {
    setEditedContent(markdown);
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!selectedNote) return;
    setIsSaving(true);
    try {
      await apiClient.put(`/notes/${selectedNote.id}`, { contentHtml: editedContent });
      message.success(t('messages:save_success', { defaultValue: 'Saved successfully' }));
      setIsEditing(false);
      // Re-fetch the note to get updated content_html path from backend
      const response = await apiClient.get(`/notes/${selectedNote.id}`);
      const updatedNote = response.data?.data || response.data;
      if (updatedNote?.id) {
        setSelectedNote(updatedNote);
        const contentResult = await loadNoteContent(updatedNote, apiClient, t, isFilePath);
        setRealContent(contentResult.content || '');
      }
    } catch (error) {
      console.error('Failed to save note content:', error);
      message.error(t('messages:save_failed', { defaultValue: 'Failed to save' }));
    } finally {
      setIsSaving(false);
    }
  }, [selectedNote, editedContent, t]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedContent('');
  }, []);

  // 处理笔记点击（从 DashboardQA）
  const handleNoteClick = async (clickedNoteId) => {
    if (!clickedNoteId) return;
    
    // 更新 sessionStorage（通过 POST 方式传递，不显示在 URL 中）
    sessionStorage.setItem('noteContent_noteId', clickedNoteId.toString());
    
    // 加载新笔记数据（通过 POST 请求）
    await loadNoteData(clickedNoteId);
  };

  // 处理 QA 主题点击
  const handleQATopicClick = async (clickedNoteId) => {
    await handleNoteClick(clickedNoteId);
  };

  // 处理标签页切换（从 DashboardQA）
  const handleTabChange = (key) => {
    // 如果需要，可以在这里处理标签页切换逻辑
    console.log('Tab changed to:', key);
  };

  // 获取当前筛选状态
  const currentFilterState = {
    selectedFolder: getSelectedFolder(),
    selectedTopic: getSelectedTopic(),
    selectedKB: getSelectedKB(),
    selectedTag: getSelectedTag(),
    searchKeyword: getSearchKeyword()
  };

  // 设置页面标题
  useEffect(() => {
    document.title = selectedNote?.title 
      ? `${selectedNote.title} - noteContent` 
      : 'noteContent';
  }, [selectedNote]);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!selectedNote) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        padding: '20px',
        textAlign: 'center'
      }}>
        <h2 style={{ color: '#ff4d4f', marginBottom: '16px' }}>
          {t('dashboard:no_note_selected', { defaultValue: 'No note selected' })}
        </h2>
        <p style={{ color: '#666', fontSize: '16px' }}>
          {t('dashboard:please_select_note', { defaultValue: 'Please select a note to view its content.' })}
        </p>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      overflow: 'hidden',
      width: '100%',
      minWidth: '1000px',
      margin: '0 auto'
    }}>
      <CardContent
        selectedNote={selectedNote}
        realContent={realContent}
        isLoadingContent={isLoadingContent}
        onNoteClick={handleNoteClick}
        onQATopicClick={handleQATopicClick}
        onTabChange={handleTabChange}
        currentFilterState={currentFilterState}
        isEditing={isEditing}
        htmlContent={editedContent}
        isSaving={isSaving}
        onStartEdit={handleStartEdit}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        onContentChange={handleContentChange}
        initialTool={initialTool}
      />
    </div>
  );
};

export default NoteContentPage;

